import { IconChevronLeft, IconChevronRight, IconTrash, IconX } from '@tabler/icons-react';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Xray } from '@/types/api';
import { XrayImage } from './XrayImage';

type Props = {
  xrays: Xray[];
  index: number;
  label: (xray: Xray) => string;
  image: (xray: Xray) => Promise<Blob>;
  onIndex: (index: number) => void;
  onClose: () => void;
  onDelete: (xray: Xray) => Promise<void>;
};

/** One X-ray over the whole screen; arrows (or ← →) step through the others. */
export function XrayLightbox({ xrays, index, label, image, onIndex, onClose, onDelete }: Props) {
  const { t } = useTranslation();
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const xray = xrays[index];
  const hasPrev = index > 0;
  const hasNext = index < xrays.length - 1;

  // A different image never inherits a half-done delete.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the shown image changes
  useEffect(() => setConfirming(false), [xray?.clientId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onIndex(index - 1);
      if (e.key === 'ArrowRight' && hasNext) onIndex(index + 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, hasPrev, hasNext, onIndex, onClose]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, []);

  if (!xray) return null;

  const remove = async () => {
    setBusy(true);
    await onDelete(xray);
    setBusy(false);
  };

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      ref={ref}
      tabIndex={-1}
    >
      <div className="lightbox__bar">
        <div className="lightbox__title">
          <strong id={titleId}>{label(xray)}</strong>
          <span className="small">
            {xray.createdBy.name} · {index + 1} / {xrays.length}
          </span>
        </div>
        {confirming ? (
          <button
            type="button"
            className="btn btn--sm btn--primary"
            disabled={busy}
            onClick={remove}
          >
            <IconTrash size={18} aria-hidden="true" />
            {t('chart.xrays.confirmDelete')}
          </button>
        ) : (
          <button
            type="button"
            className="icon-btn lightbox__btn"
            onClick={() => setConfirming(true)}
            aria-label={t('chart.xrays.delete')}
            title={t('chart.xrays.delete')}
          >
            <IconTrash size={22} />
          </button>
        )}
        <button
          type="button"
          className="icon-btn lightbox__btn"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <IconX size={24} />
        </button>
      </div>
      <div className="lightbox__stage">
        <XrayImage
          key={xray.id}
          source={xray.id}
          load={() => image(xray)}
          alt={t('chart.xrays.alt', { label: label(xray) })}
          className="lightbox__image"
        />
        {hasPrev && (
          <button
            type="button"
            className="icon-btn lightbox__nav lightbox__nav--prev"
            onClick={() => onIndex(index - 1)}
            aria-label={t('chart.xrays.prev')}
          >
            <IconChevronLeft size={28} />
          </button>
        )}
        {hasNext && (
          <button
            type="button"
            className="icon-btn lightbox__nav lightbox__nav--next"
            onClick={() => onIndex(index + 1)}
            aria-label={t('chart.xrays.next')}
          >
            <IconChevronRight size={28} />
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
